// Active-filter chip derivation. Pure — no React, no DOM.
//
// Plan 05-05 FILT-05: chips below FilterBar represent each non-default filter.
// Clicking the × resets that filter to its default with push (history entry).

import type { FilterState } from '@/types/db';
import { DEFAULT_FILTERS } from './filterDefaults';
import { NEAREST_CATEGORY_BY_VALUE } from '@/lib/nearest-constants';

export interface ActiveFilterChipSpec {
  /** Stable React key — unique across the chip set for a given filter shape. */
  key: string;
  /** Human-facing label rendered inside the chip. */
  label: string;
  /**
   * Partial filter patch that, when merged into the current FilterState via
   * `setFilters({...filters, ...reset})`, removes this chip's contribution.
   */
  reset: Partial<FilterState>;
}

/** Display-case a county slug ("palm-beach" → "Palm Beach"). */
function titleCaseCounty(slug: string): string {
  return slug
    .split('-')
    .map((part) => (part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ');
}

const SORT_LABELS: Record<string, string> = {
  composite: 'Best Fit',
  dog: 'Dog Fit',
  walk_ent: 'Walk + Ent',
  recency: 'Recency',
  price: 'Price',
};

/** Short category labels for the cats-subset chip. */
const SHORT_CAT: Record<string, string> = {
  dog_park: 'Parks',
  beach: 'Beaches',
  daycare: 'Daycare',
  bar: 'Bars',
};

const RECENCY_LABELS: Record<string, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

/**
 * Compute the active-chip set from a FilterState. Each non-default field
 * yields one or more chips; defaults yield none.
 *
 * Order (UI-SPEC § 3.9 — left to right, drives natural reading):
 * 1. Radius (if ≠ default 0.5)
 * 2. Sort (if ≠ composite)
 * 3. Each county chip (one per slug)
 * 4. Recency (if ≠ all)
 *
 * `cats` does NOT generate chips in v2.0 — the four canonical categories are
 * always part of the deep-link contract; per UI-SPEC § 3.7 there is no
 * category-toggle UI in Phase 05. (Future: cats chips when category toggles ship.)
 */
export function computeActiveChips(filters: FilterState): ActiveFilterChipSpec[] {
  const chips: ActiveFilterChipSpec[] = [];

  if (Math.abs(filters.radius - DEFAULT_FILTERS.radius) > 0.01) {
    chips.push({
      key: 'radius',
      label: `Radius ${filters.radius.toFixed(1)} mi`,
      reset: { radius: DEFAULT_FILTERS.radius },
    });
  }

  if (filters.sort !== DEFAULT_FILTERS.sort) {
    const sortLabel =
      filters.sort === 'nearest'
        ? `Nearest ${NEAREST_CATEGORY_BY_VALUE[filters.nearestCat]?.label ?? 'amenity'}`
        : (SORT_LABELS[filters.sort] ?? 'Best Fit');
    chips.push({
      key: 'sort',
      label: `Sort: ${sortLabel}`,
      reset: { sort: DEFAULT_FILTERS.sort, nearestCat: DEFAULT_FILTERS.nearestCat },
    });
  }

  // Category subset — only when the user has narrowed off the default 4.
  if (filters.cats.join(',') !== DEFAULT_FILTERS.cats.join(',')) {
    const names = filters.cats.map((c) => SHORT_CAT[c] ?? c);
    chips.push({
      key: 'cats',
      label: names.length === 0 ? 'No amenities' : `Amenities: ${names.join(', ')}`,
      reset: { cats: DEFAULT_FILTERS.cats.slice() },
    });
  }

  // In-radius-only filter (mirrors the map's radius-only toggle).
  if (filters.inRadiusOnly && !DEFAULT_FILTERS.inRadiusOnly) {
    chips.push({
      key: 'inRadiusOnly',
      label: 'Within radius only',
      reset: { inRadiusOnly: false },
    });
  }

  for (const slug of filters.county) {
    chips.push({
      key: `county:${slug}`,
      label: titleCaseCounty(slug),
      reset: { county: filters.county.filter((c) => c !== slug) },
    });
  }

  if (filters.recency !== DEFAULT_FILTERS.recency) {
    const label = RECENCY_LABELS[filters.recency];
    if (label) {
      chips.push({
        key: 'recency',
        label,
        reset: { recency: DEFAULT_FILTERS.recency },
      });
    }
  }

  return chips;
}
